import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Package, FileText, CalendarX2, Upload, Check, X, Loader2, FileDown, Settings2, Plus, Trash2, MessageSquare, CalendarCheck, Mail } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { adminGet, adminPost, adminPut, reportError, formatDate, BACKEND_URL } from './adminApi';

const TYPE_UI = { supply: { label: 'Fournitures', icon: Package }, document: { label: 'Demande de document', icon: FileText }, unavailability: { label: 'Indisponibilité', icon: CalendarX2 }, upload: { label: 'Envoi de document', icon: Upload } };
const STATUS_UI = {
  pending: { label: 'En attente', className: 'bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]' },
  approved: { label: 'Acceptée', className: 'bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage))]' },
  done: { label: 'Traitée', className: 'bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]' },
  refused: { label: 'Refusée', className: 'bg-destructive text-destructive-foreground hover:bg-destructive' },
  cancelled: { label: 'Annulée', className: 'bg-muted text-muted-foreground hover:bg-muted' },
};
const adminFileUrl = (requestId, fileId, key) => `${BACKEND_URL}/api/admin/easer/requests/${requestId}/files/${fileId}?key=${encodeURIComponent(key)}`;

/** Admin: GRH requests from Easers (supplies, documents, unavailabilities, uploaded documents) — decide, mark done, download PDFs, edit the supply catalogue. */
export function AdminEaserRequests({ adminKey }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('pending');
  const [type, setType] = useState('all');
  const [action, setAction] = useState(null); // {request, mode: 'approved'|'refused'|'done'}
  const [catalogOpen, setCatalogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/easer/requests', adminKey, { status: status === 'all' ? undefined : status, type: type === 'all' ? undefined : type });
      setData(res.data);
    } catch (err) {
      reportError('demandes GRH', err, () => toast.error('Impossible de charger les demandes'));
      setData((d) => d || { items: [], counts: {} });
    }
  }, [adminKey, status, type]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5" data-testid="admin-easer-requests">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Demandes GRH des Easers
            {data?.counts?.pending > 0 && <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]" data-testid="requests-pending-count">{data.counts.pending} en attente</Badge>}
          </h3>
          <p className="text-sm text-muted-foreground">Fournitures, documents, indisponibilités et documents envoyés (arrêts de travail… convertis en PDF). Une indisponibilité acceptée est inscrite dans l'agenda Dolibarr de l'Easer.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setCatalogOpen(true)} data-testid="requests-catalog-open"><Settings2 className="h-4 w-4 mr-1" /> Catalogue fournitures</Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex rounded-lg border border-border p-0.5">
          {[['pending', 'En attente'], ['approved', 'Acceptées'], ['done', 'Traitées'], ['refused', 'Refusées'], ['all', 'Toutes']].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setStatus(id)} data-testid={`requests-status-${id}`} className={`px-2.5 h-8 rounded-md text-xs font-medium ${status === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground'}`}>{label}</button>
          ))}
        </div>
        <div className="flex rounded-lg border border-border p-0.5">
          {[['all', 'Tous types'], ...Object.entries(TYPE_UI).map(([id, t]) => [id, t.label])].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setType(id)} data-testid={`requests-type-${id}`} className={`px-2.5 h-8 rounded-md text-xs font-medium ${type === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground'}`}>{label}</button>
          ))}
        </div>
      </div>

      {data === null ? <Skeleton className="h-24" /> : data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="admin-requests-empty">Aucune demande pour ce filtre.</p>
      ) : (
        <ul className="space-y-3" data-testid="admin-requests-list">
          {data.items.map((r) => {
            const t = TYPE_UI[r.type] || TYPE_UI.supply;
            const st = STATUS_UI[r.status] || STATUS_UI.pending;
            return (
              <li key={r.id} className="rounded-xl border border-border p-3" data-testid="admin-request-item" data-type={r.type} data-status={r.status}>
                <div className="flex flex-wrap items-start gap-3">
                  <span className="h-9 w-9 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><t.icon className="h-4 w-4" /></span>
                  <div className="min-w-[220px] flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium" data-testid="admin-request-user">{r.user_name}</p>
                      <span className="text-xs text-muted-foreground">{t.label} · {formatDate(r.created_at, true)}</span>
                      <Badge className={st.className} data-testid="admin-request-status">{r.status === 'done' ? r.done_label : st.label}</Badge>
                      {r.email_sent === false && <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" title="E-mail agence non envoyé (SMTP)"><Mail className="h-3 w-3" /> e-mail non envoyé</span>}
                    </div>
                    <p className="text-sm mt-1" data-testid="admin-request-summary">{r.summary}</p>
                    {r.payload?.items?.length > 0 && <ul className="text-sm text-muted-foreground list-disc pl-5 mt-1">{r.payload.items.map((i) => <li key={i.id}>{i.qty} {i.unit} {i.label}</li>)}{r.payload.free_text && <li>{r.payload.free_text}</li>}</ul>}
                    {r.payload?.details && r.type !== 'supply' && <p className="text-sm text-muted-foreground mt-1">{r.payload.details}</p>}
                    {r.files?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {r.files.map((f) => <Button key={f.id} asChild size="sm" variant="outline"><a href={adminFileUrl(r.id, f.id, adminKey)} target="_blank" rel="noopener noreferrer" data-testid="admin-request-file"><FileDown className="h-4 w-4 mr-1" /> {f.filename}{f.pages ? ` · ${f.pages} p.` : ''}</a></Button>)}
                      </div>
                    )}
                    {r.dolibarr_event_id && <p className="text-xs text-[hsl(var(--primary))] mt-1 inline-flex items-center gap-1"><CalendarCheck className="h-3 w-3" /> Inscrite dans l'agenda Dolibarr (événement #{r.dolibarr_event_id})</p>}
                    {r.admin_comment && <p className="text-sm mt-2 rounded-md bg-[hsl(var(--secondary))]/70 p-2 inline-flex items-start gap-1.5"><MessageSquare className="h-3.5 w-3.5 mt-0.5" /> {r.admin_comment}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {r.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => setAction({ request: r, mode: 'approved' })} data-testid="admin-request-approve"><Check className="h-4 w-4 mr-1" /> {r.type === 'unavailability' ? 'Valider (agenda)' : 'Accepter'}</Button>
                        <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setAction({ request: r, mode: 'refused' })} data-testid="admin-request-refuse"><X className="h-4 w-4 mr-1" /> Refuser</Button>
                      </>
                    )}
                    {['pending', 'approved'].includes(r.status) && r.type !== 'unavailability' && (
                      <Button size="sm" variant="outline" onClick={() => setAction({ request: r, mode: 'done' })} data-testid="admin-request-done"><Check className="h-4 w-4 mr-1" /> {r.done_label}</Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {action && <ActionDialog action={action} adminKey={adminKey} onClose={() => setAction(null)} onSaved={() => { setAction(null); load(); }} />}
      {catalogOpen && <CatalogDialog adminKey={adminKey} onClose={() => setCatalogOpen(false)} />}
    </Card>
  );
}

function ActionDialog({ action, adminKey, onClose, onSaved }) {
  const { request: r, mode } = action;
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const titles = { approved: r.type === 'unavailability' ? 'Valider l\'indisponibilité' : 'Accepter la demande', refused: 'Refuser la demande', done: `Marquer « ${r.done_label} »` };

  const save = async () => {
    setSaving(true);
    try {
      if (mode === 'done') await adminPost(`/api/admin/easer/requests/${r.id}/done`, adminKey, {}, { comment });
      else await adminPost(`/api/admin/easer/requests/${r.id}/decision`, adminKey, {}, { status: mode, comment });
      toast.success(mode === 'refused' ? 'Demande refusée' : mode === 'approved' && r.type === 'unavailability' ? 'Indisponibilité inscrite dans l\'agenda' : 'Demande mise à jour');
      onSaved();
    } catch (err) {
      reportError('décision demande', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="request-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Space_Grotesk']">{titles[mode]}</DialogTitle>
          <DialogDescription>{r.user_name} — {r.summary}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="req-comment">Commentaire pour l'Easer {mode === 'refused' ? '(recommandé)' : '(facultatif)'}</Label>
          <Textarea id="req-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1500} placeholder={mode === 'done' ? 'Ex. fournitures déposées à l\'agence, à récupérer…' : 'Ex. disponible à l\'agence jeudi'} data-testid="request-action-comment" />
        </div>
        {mode === 'approved' && r.type === 'unavailability' && <p className="text-xs text-muted-foreground">Un événement « INDISPONIBILITÉ » sera créé dans l'agenda Dolibarr de l'Easer et apparaîtra dans son planning.</p>}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="request-action-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving} variant={mode === 'refused' ? 'destructive' : 'default'} data-testid="request-action-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Confirmer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogDialog({ adminKey, onClose }) {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGet('/api/admin/easer/supply-catalog', adminKey).then((r) => setItems(r.data.items)).catch((err) => { reportError('catalogue', err, () => toast.error('Catalogue indisponible')); setItems([]); });
  }, [adminKey]);

  const update = (i, k, v) => setItems((list) => list.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const add = () => setItems((list) => [...list, { id: `item_${Date.now().toString(36)}`, label: '', unit: 'pièce(s)', enabled: true }]);
  const remove = (i) => setItems((list) => list.filter((_, j) => j !== i));

  const save = async () => {
    const clean = items.filter((it) => it.label.trim()).map((it) => ({ ...it, label: it.label.trim(), unit: it.unit.trim() }));
    setSaving(true);
    try {
      await adminPut('/api/admin/easer/supply-catalog', adminKey, {}, { items: clean });
      toast.success('Catalogue enregistré');
      onClose();
    } catch (err) {
      reportError('catalogue', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="catalog-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Space_Grotesk']">Catalogue des fournitures</DialogTitle>
          <DialogDescription>Articles proposés aux Easers dans « Fournitures ». Décochez pour masquer un article sans le supprimer.</DialogDescription>
        </DialogHeader>
        {items === null ? <Skeleton className="h-32" /> : (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it.id} className="flex items-center gap-2" data-testid="catalog-row">
                <Checkbox checked={it.enabled} onCheckedChange={(v) => update(i, 'enabled', !!v)} aria-label="Actif" data-testid="catalog-enabled" />
                <Input value={it.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="Article" className="flex-1" data-testid="catalog-label" />
                <Input value={it.unit} onChange={(e) => update(i, 'unit', e.target.value)} placeholder="unité" className="w-28" data-testid="catalog-unit" />
                <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(i)} aria-label="Supprimer" data-testid="catalog-remove"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={add} data-testid="catalog-add"><Plus className="h-4 w-4 mr-1" /> Ajouter un article</Button>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>Fermer</Button>
          <Button onClick={save} disabled={saving || items === null} data-testid="catalog-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
